import { useState } from 'react';
import { X, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Slider } from '../ui/slider';
import { Label } from '../ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { formatPrice, cn } from '../../lib/utils';

const categories = [
  { id: 'Domestic', label: 'Domestic Tours', count: 0 },
  { id: 'International', label: 'International Tours', count: 0 },
  { id: 'Honeymoon', label: 'Honeymoon Packages', count: 0 },
  { id: 'Corporate', label: 'Corporate Tours', count: 0 },
  { id: 'Custom', label: 'Custom Tours', count: 0 },
];

const durations = [
  { id: '1-3', label: '1-3 Days', min: 1, max: 3 },
  { id: '4-6', label: '4-6 Days', min: 4, max: 6 },
  { id: '7-10', label: '7-10 Days', min: 7, max: 10 },
  { id: '11-14', label: '11-14 Days', min: 11, max: 14 },
  { id: '15+', label: '15+ Days', min: 15, max: 30 },
];

// Filter Content Component - defined outside to avoid re-creation on render
function FilterContent({ 
  filters, 
  openSections, 
  toggleSection, 
  handleCategoryChange, 
  handleDurationChange, 
  handlePriceChange,
  hasActiveFilters,
  onClearFilters,
  isMobile,
  onClose
}) {
  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <h3 className="text-lg font-semibold text-[#1A1A1A]" style={{ fontFamily: 'Playfair Display, serif' }}>
          Filters
        </h3>
        {isMobile && (
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="p-4 space-y-6">
        {/* Category Filter */}
        <Collapsible open={openSections.category} onOpenChange={() => toggleSection('category')}>
          <CollapsibleTrigger className="flex items-center justify-between w-full py-2">
            <span className="font-medium text-[#1A1A1A]">Category</span>
            {openSections.category ? (
              <ChevronUp className="w-4 h-4 text-[#4A4A4A]" />
            ) : (
              <ChevronDown className="w-4 h-4 text-[#4A4A4A]" />
            )}
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-3 space-y-3">
            {categories.map((category) => (
              <div key={category.id} className="flex items-center space-x-3">
                <Checkbox
                  id={`category-${category.id}`}
                  checked={(filters.categories || []).includes(category.id)}
                  onCheckedChange={(checked) => handleCategoryChange(category.id, checked)}
                  className="border-gray-300 data-[state=checked]:bg-[#FF6B35] data-[state=checked]:border-[#FF6B35]"
                />
                <Label 
                  htmlFor={`category-${category.id}`}
                  className="text-sm text-[#4A4A4A] cursor-pointer flex-1"
                >
                  {category.label}
                </Label>
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>

        <div className="border-t border-gray-100" />

        {/* Price Range Filter */}
        <Collapsible open={openSections.price} onOpenChange={() => toggleSection('price')}>
          <CollapsibleTrigger className="flex items-center justify-between w-full py-2">
            <span className="font-medium text-[#1A1A1A]">Price Range</span>
            {openSections.price ? (
              <ChevronUp className="w-4 h-4 text-[#4A4A4A]" />
            ) : (
              <ChevronDown className="w-4 h-4 text-[#4A4A4A]" />
            )}
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-4">
            <Slider
              value={[filters.minPrice || 0, filters.maxPrice || 500000]}
              onValueChange={handlePriceChange}
              max={500000}
              min={0}
              step={5000}
              className="w-full"
            />
            <div className="flex justify-between mt-3">
              <div className="bg-gray-50 rounded-lg px-3 py-2">
                <span className="text-xs text-[#4A4A4A]">Min</span>
                <p className="text-sm font-semibold text-[#1A1A1A]">
                  {formatPrice(filters.minPrice || 0)}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg px-3 py-2 text-right">
                <span className="text-xs text-[#4A4A4A]">Max</span>
                <p className="text-sm font-semibold text-[#1A1A1A]">
                  {formatPrice(filters.maxPrice || 500000)}
                </p>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        <div className="border-t border-gray-100" />

        {/* Duration Filter */}
        <Collapsible open={openSections.duration} onOpenChange={() => toggleSection('duration')}>
          <CollapsibleTrigger className="flex items-center justify-between w-full py-2">
            <span className="font-medium text-[#1A1A1A]">Duration</span>
            {openSections.duration ? (
              <ChevronUp className="w-4 h-4 text-[#4A4A4A]" />
            ) : (
              <ChevronDown className="w-4 h-4 text-[#4A4A4A]" />
            )}
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-3 space-y-3">
            {durations.map((duration) => (
              <div key={duration.id} className="flex items-center space-x-3">
                <Checkbox
                  id={`duration-${duration.id}`}
                  checked={filters.durationId === duration.id}
                  onCheckedChange={(checked) => handleDurationChange(duration.id, checked)}
                  className="border-gray-300 data-[state=checked]:bg-[#FF6B35] data-[state=checked]:border-[#FF6B35]"
                />
                <Label 
                  htmlFor={`duration-${duration.id}`}
                  className="text-sm text-[#4A4A4A] cursor-pointer flex-1"
                >
                  {duration.label}
                </Label>
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>
      </div>

      {/* Clear Filters Button */}
      {hasActiveFilters && (
        <div className="p-4 border-t border-gray-100">
          <Button
            variant="outline"
            onClick={onClearFilters}
            className="w-full border-[#FF6B35] text-[#FF6B35] hover:bg-[#FF6B35] hover:text-white rounded-xl"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Clear All Filters
          </Button>
        </div>
      )}
    </>
  );
}

export function FilterSidebar({ 
  filters, 
  onFilterChange, 
  onClearFilters,
  isOpen,
  onClose,
  isMobile = false 
}) {
  const [openSections, setOpenSections] = useState({
    category: true,
    price: true,
    duration: true,
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleCategoryChange = (categoryId, checked) => {
    const newCategories = checked
      ? [...(filters.categories || []), categoryId]
      : (filters.categories || []).filter(c => c !== categoryId);
    onFilterChange({ categories: newCategories });
  };

  const handleDurationChange = (durationId, checked) => {
    const duration = durations.find(d => d.id === durationId);
    if (checked && duration) {
      onFilterChange({ 
        minDuration: duration.min, 
        maxDuration: duration.max,
        durationId: durationId 
      });
    } else {
      onFilterChange({ 
        minDuration: null, 
        maxDuration: null,
        durationId: null 
      });
    }
  };

  const handlePriceChange = (values) => {
    onFilterChange({ minPrice: values[0], maxPrice: values[1] });
  };

  const hasActiveFilters = 
    (filters.categories && filters.categories.length > 0) ||
    filters.minPrice > 0 ||
    filters.maxPrice < 500000 ||
    filters.durationId;

  const contentProps = {
    filters,
    openSections,
    toggleSection,
    handleCategoryChange,
    handleDurationChange,
    handlePriceChange,
    hasActiveFilters,
    onClearFilters,
    isMobile,
    onClose
  };

  // Mobile Drawer
  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        <div
          className={cn(
            'fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 lg:hidden',
            isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}
          onClick={onClose}
        />

        {/* Drawer */}
        <div
          className={cn(
            'fixed left-0 top-0 h-full w-80 bg-white z-50 shadow-2xl transition-transform duration-300 lg:hidden overflow-y-auto',
            isOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          <FilterContent {...contentProps} />
        </div>
      </>
    );
  }

  // Desktop Sidebar
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 sticky top-24 overflow-hidden">
      <FilterContent {...contentProps} />
    </div>
  );
}
